
import { getTableRecords } from '@/services/teableApi';
import { getTeableConfig, isTeableConfigured } from '@/config/teableConfig';
import { cacheService } from './cacheService';
import { rateLimiter, RATE_LIMIT_CONFIGS } from '@/utils/rateLimiter';
import { sanitizeInput } from '@/utils/inputValidation';
import { secureLog } from '@/utils/secureLogger';

/**
 * Converte una data in formato europeo (dd/mm/yyyy HH:mm) in oggetto Date
 * Con gestione sicura degli errori
 */
const parseEuropeanDate = (dateString: string): Date => {
  if (!dateString || typeof dateString !== 'string') {
    return new Date(0); // Data minima per ordinamento
  }
  
  try {
    // Formato: "7/6/2025 23:51" o "02/09/2025 13:18"
    const parts = dateString.trim().split(' ');
    if (parts.length !== 2) return new Date(0);
    
    const [datePart, timePart] = parts;
    const datePieces = datePart.split('/');
    const timePieces = timePart.split(':');
    
    if (datePieces.length !== 3 || timePieces.length !== 2) {
      return new Date(0);
    }
    
    const [day, month, year] = datePieces.map(p => parseInt(p, 10));
    const [hours, minutes] = timePieces.map(p => parseInt(p, 10));
    
    // Validazione valori
    if (isNaN(day) || isNaN(month) || isNaN(year) || isNaN(hours) || isNaN(minutes)) {
      return new Date(0);
    }
    
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return new Date(0);
    }
    
    // JavaScript Date constructor: new Date(year, monthIndex, day, hours, minutes)
    // monthIndex è 0-based (0 = gennaio, 11 = dicembre)
    return new Date(year, month - 1, day, hours, minutes);
  } catch (error) {
    secureLog.error('Errore parsing data:', dateString);
    return new Date(0);
  }
};

/**
 * Servizio specializzato per le promozioni
 */

/**
 * Ottiene tutte le promozioni attive (con caching e rate limiting)
 */
export const fetchPromos = async (): Promise<any[]> => {
  // Rate limiting
  return rateLimiter.execute('fetchPromos', RATE_LIMIT_CONFIGS.READ_RECORDS, async () => {
    // Usa cache con TTL di 10 minuti per promozioni
    return cacheService.getOrFetch('all_promos', async () => {
    if (!isTeableConfigured()) {
      const { mockPromos } = await import('@/utils/mockData');
      return mockPromos.map(promo => ({
        id: promo.id,
        fields: {
          Nome: promo.name,
          Descrizione: promo.description,
          Immagine: promo.image,
          Link: '#',
          DataFine: null
        }
      }));
    }

    try {
      const config = getTeableConfig();
      const records = await getTableRecords(config.promoTableId);
      
      // Filtra le promozioni scadute
      const today = new Date();
      const activePromos = records.filter((record: any) => {
        const dataFine = record.fields?.DataFine;
        if (!dataFine) return true;
        
        const dateParts = dataFine.split('/');
        if (dateParts.length === 3) {
          const [day, month, year] = dateParts;
          const endDate = new Date(`${month}/${day}/${year}`);
          return endDate >= today;
        }
        return true;
      });
      
      return activePromos;
    } catch (error) {
      secureLog.error('Errore fetchPromos:', error);
      throw error;
    }
  }, 3 * 60 * 1000); // Cache per 3 minuti (ottimizzato per velocità e freschezza)
  });
};

/**
 * Ottiene le ultime N promozioni inserite (con caching e rate limiting)
 */
export const fetchLatestPromosN = async (count: number = 3): Promise<any[]> => {
  const cacheKey = `latest_promos_${count}`;
  return rateLimiter.execute(`fetchLatestPromos${count}`, RATE_LIMIT_CONFIGS.READ_RECORDS, async () => {
    return cacheService.getOrFetch(cacheKey, async () => {
    if (!isTeableConfigured()) {
      const { mockPromos } = await import('@/utils/mockData');
      return mockPromos.slice(0, count).map(promo => ({
        id: promo.id,
        fields: {
          Nome: promo.name,
          Descrizione: promo.description,
          Immagine: promo.image,
          Link: '#',
          DataFine: null
        }
      }));
    }

    try {
      const config = getTeableConfig();
      const records = await getTableRecords(config.promoTableId, {
        maxRecords: 100,
        sort: []
      });
      
      // Ordina per DataCreazione
      const sortedRecords = records.sort((a: any, b: any) => {
        const dateA = parseEuropeanDate(a.fields?.DataCreazione);
        const dateB = parseEuropeanDate(b.fields?.DataCreazione);
        return dateB.getTime() - dateA.getTime();
      });
      
      // Filtra le promozioni scadute
      const today = new Date();
      const activePromos = sortedRecords.filter((record: any) => {
        const dataFine = record.fields?.DataFine;
        if (!dataFine) return true;
        
        const dateParts = dataFine.split('/');
        if (dateParts.length === 3) {
          const [day, month, year] = dateParts;
          const endDate = new Date(`${month}/${day}/${year}`);
          return endDate >= today;
        }
        return true;
      });
      
      return activePromos.slice(0, count);
    } catch (error) {
      secureLog.error('Errore fetchLatestPromosN:', error);
      return [];
    }
  }, 3 * 60 * 1000); // Cache per 3 minuti (ottimizzato per velocità e freschezza)
  });
};

/**
 * Ottiene le ultime 3 promozioni inserite (con caching e rate limiting)
 */
export const fetchLatestPromos = async (): Promise<any[]> => {
  return fetchLatestPromosN(3);
};

/**
 * Ottiene le ultime 20 promozioni inserite (per la sezione PROMO)
 */
export const fetchTop20Promos = async (): Promise<any[]> => {
  return fetchLatestPromosN(20);
};

/**
 * Cerca promozioni per categoria
 */
export const fetchPromosByCategory = async (category: string): Promise<any[]> => {
  const cacheKey = `category_${category.toLowerCase()}`;
  return rateLimiter.execute(`fetchCategory_${category}`, RATE_LIMIT_CONFIGS.READ_RECORDS, async () => {
    return cacheService.getOrFetch(cacheKey, async () => {
      try {
        const allPromos = await fetchPromos();
        return allPromos.filter((promo: any) => {
          const cat = promo.fields?.Categorie?.toLowerCase()?.trim();
          return cat === category.toLowerCase().trim();
        });
      } catch (error) {
        secureLog.error('Errore fetchPromosByCategory:', error);
        return [];
      }
    }, 3 * 60 * 1000);
  });
};

/**
 * Cerca promozioni per termine (con caching, validazione e rate limiting)
 */
export const searchPromos = async (searchTerm: string): Promise<any[]> => {
  // Sanitizza l'input di ricerca
  const sanitizedTerm = sanitizeInput(searchTerm);
  
  if (!sanitizedTerm || sanitizedTerm.length < 2) {
    throw new Error('Il termine di ricerca deve contenere almeno 2 caratteri');
  }
  
  if (sanitizedTerm.length > 100) {
    throw new Error('Il termine di ricerca è troppo lungo');
  }
  
  const cacheKey = `search_${sanitizedTerm.toLowerCase()}`;
  
  return rateLimiter.execute(`search_${sanitizedTerm}`, RATE_LIMIT_CONFIGS.SEARCH_PROMOS, async () => {
    return cacheService.getOrFetch(cacheKey, async () => {
    if (!isTeableConfigured()) {
      const { searchPromos: mockSearch } = await import('@/utils/mockData');
      const mockResults = mockSearch(searchTerm);
      return mockResults.map(promo => ({
        id: promo.id,
        fields: {
          Nome: promo.name,
          Descrizione: promo.description,
          Immagine: promo.image,
          Link: '#',
          DataFine: null
        }
      }));
    }

    try {
      const config = getTeableConfig();
      const filter = {
        conjunction: 'or',
        filterSet: [
          { fieldId: 'Nome', operator: 'contains', value: sanitizedTerm },
          { fieldId: 'Descrizione', operator: 'contains', value: sanitizedTerm }
        ]
      };
      
      const records = await getTableRecords(config.promoTableId, {
        filter: filter,
        maxRecords: 20
      });
      
      // Filtra solo le promozioni attive
      const today = new Date();
      const activePromos = records.filter((record: any) => {
        const dataFine = record.fields?.DataFine;
        if (!dataFine) return true;
        
        const dateParts = dataFine.split('/');
        if (dateParts.length === 3) {
          const [day, month, year] = dateParts;
          const endDate = new Date(`${month}/${day}/${year}`);
          return endDate >= today;
        }
        return true;
      });
      
      return activePromos;
    } catch (error) {
      secureLog.error('Errore searchPromos:', error);
      // Fallback
      const allPromos = await fetchPromos();
      const searchLower = sanitizedTerm.toLowerCase();
      return allPromos.filter((promo: any) => {
        const nome = promo.fields?.Nome?.toLowerCase() || '';
        const descrizione = promo.fields?.Descrizione?.toLowerCase() || '';
        return nome.includes(searchLower) || descrizione.includes(searchLower);
      });
    }
  }, 2 * 60 * 1000); // Cache per 2 minuti
  });
};

/**
 * Ottiene una singola promozione per ID o nome (con caching)
 */
export const getPromoById = async (promoId: string): Promise<any | null> => {
  const cacheKey = `promo_${promoId}`;
  
  return cacheService.getOrFetch(cacheKey, async () => {
    try {
      const allPromos = await fetchPromos();
      const promo = allPromos.find((p: any) => 
        p.id === promoId || 
        p.fields?.id === promoId ||
        p.fields?.Nome === promoId ||
        p.name === promoId
      );
      
      return promo || null;
    } catch (error) {
      secureLog.error('Errore getPromoById:', error);
      return null;
    }
  }, 5 * 60 * 1000); // Cache per 5 minuti
};

/**
 * Ottiene punti vendita per promozione e provincia dalla tabella Teable specifica
 */
export const getPromosByProvince = async (promoId: string, provinceId: string): Promise<any[]> => {
  const cacheKey = `province_${promoId}_${provinceId}`;
  
  return cacheService.getOrFetch(cacheKey, async () => {
    try {
      secureLog.log('🔍 getPromosByProvince - Cercando punti vendita:', { promoId, provinceId });
      
      // Ottieni la promozione per trovare il TableId
      const promo = await getPromoById(promoId);
      if (!promo || !promo.fields?.TableId) {
        secureLog.error('❌ Promozione non trovata o senza TableId:', promoId);
        return [];
      }
      
      const tableId = promo.fields.TableId;
      secureLog.log('📋 TableId trovato:', tableId);
      
      // Usa la funzione di teableRecords per recuperare i dati della provincia
      const { getAllRecordsForProvince } = await import('@/services/teableRecords');
      const records = await getAllRecordsForProvince(tableId, provinceId);
      
      secureLog.log(`✅ Trovati ${records.length} punti vendita per ${promoId} in provincia ${provinceId}`);
      
      // Filtra solo record con dati effettivi
      const validRecords = records.filter((record: any) => {
        return !!(record.fields.Nome1 || record.fields.Località || record.fields.Insegna);
      });
      
      secureLog.log(`✅ Record validi: ${validRecords.length}`);
      
      return validRecords;
    } catch (error) {
      secureLog.error('❌ Errore getPromosByProvince:', error);
      return [];
    }
  }, 5 * 60 * 1000); // Cache per 5 minuti
};

/**
 * Crea un nuovo record in una promozione (compatibilità con codice esistente)
 */
export const createPromo = async (promoName: string, promoData: any, turnstileToken?: string): Promise<{ success: boolean; position: number; error?: string }> => {
  try {
    const { createRecord } = await import('@/services/teableRecords');
    
    // Usa createRecord passando il NOME della promozione e il token Turnstile
    const result = await createRecord(promoName, promoData, turnstileToken);
    
    // Invalida la cache
    cacheService.delete('all_promos');
    cacheService.delete('latest_promos');
    cacheService.delete(`province_${promoData.sigla}`);
    
    return { 
      success: true, 
      position: result.position 
    };
  } catch (error: any) {
    secureLog.error('Errore createPromo:', error);
    return {
      success: false, 
      position: 0,
      error: error.message || 'Errore durante la creazione della promozione' 
    };
  }
};
